
import { supabase } from '@/integrations/supabase/client';
import { SparcAgentConfig, SparcSession, SparcInteraction } from '@/types/sparc';
import { PostgrestSingleResponse } from '@supabase/supabase-js';

// Export SparcInteraction for use in other files
export type { SparcInteraction };

// Create a new SPARC session
export const createSession = async (
  userId: string,
  name: string,
  config: SparcAgentConfig
): Promise<SparcSession | null> => {
  try {
    // Convert config to JSON to satisfy Supabase's Json type
    const configJson = config as unknown as Record<string, any>;
    
    // Insert the new session into the database
    const { data, error } = await supabase
      .from('sparc_sessions')
      .insert({
        user_id: userId,
        name,
        config: configJson,
        is_active: true
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating session:', error);
      throw error;
    }

    // Transform the data to ensure config is parsed properly
    const session: SparcSession = {
      ...data,
      config: typeof data.config === 'string' 
        ? JSON.parse(data.config) 
        : data.config
    };

    return session;
  } catch (error) {
    console.error('Error creating session:', error);
    return null;
  }
};

// Get an active session for a user
export const getActiveSession = async (userId: string): Promise<SparcSession | null> => {
  try {
    const { data, error } = await supabase
      .from('sparc_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No active session found
        return null;
      }
      console.error('Error fetching active session:', error);
      throw error;
    }

    // Transform the data to ensure config is parsed properly
    const session: SparcSession = {
      ...data,
      config: typeof data.config === 'string' 
        ? JSON.parse(data.config) 
        : data.config
    };

    return session;
  } catch (error) {
    console.error('Error fetching active session:', error);
    return null;
  }
};

// Update a session
export const updateSession = async (
  sessionId: string,
  updates: Partial<SparcSession>
): Promise<SparcSession | null> => {
  try {
    // Convert config to a JSON-compatible format if it exists in updates
    const updatesForDb = { ...updates };
    if (updatesForDb.config) {
      updatesForDb.config = updatesForDb.config as unknown as Record<string, any>;
    }
    
    const { data, error } = await supabase
      .from('sparc_sessions')
      .update(updatesForDb)
      .eq('id', sessionId)
      .select()
      .single();

    if (error) {
      console.error('Error updating session:', error);
      throw error;
    }

    // Transform the data to ensure config is parsed properly
    const session: SparcSession = {
      ...data,
      config: typeof data.config === 'string' 
        ? JSON.parse(data.config) 
        : data.config
    };

    return session;
  } catch (error) {
    console.error('Error updating session:', error);
    return null;
  }
};

// Record an interaction in the session
export const recordInteraction = async (
  sessionId: string,
  taskId: string,
  taskName: string,
  query: string,
  response: string,
  model: string,
  provider: string,
  metadata: {
    executionTime?: number;
    inputTokens?: number;
    outputTokens?: number;
    agentType?: string;
    status?: 'success' | 'error' | 'warning';
    timestamp?: string;
  }
): Promise<SparcInteraction | null> => {
  try {
    // Add timestamp if not provided
    if (!metadata.timestamp) {
      metadata.timestamp = new Date().toISOString();
    }
    
    const { data, error } = await supabase
      .from('sparc_interactions')
      .insert({
        session_id: sessionId,
        task_id: taskId,
        task_name: taskName,
        query,
        response,
        model,
        provider,
        metadata
      })
      .select()
      .single();

    if (error) {
      console.error('Error recording interaction:', error);
      throw error;
    }

    // Transform the data to ensure metadata is properly parsed
    const interaction: SparcInteraction = {
      ...data,
      metadata: typeof data.metadata === 'string' 
        ? JSON.parse(data.metadata) 
        : data.metadata
    };

    return interaction;
  } catch (error) {
    console.error('Error recording interaction:', error);
    return null;
  }
};

// Get session interactions - Fixed parameter count
export const getSessionInteractions = async (sessionId: string): Promise<SparcInteraction[]> => {
  try {
    const { data, error } = await supabase
      .from('sparc_interactions')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching session interactions:', error);
      throw error;
    }

    // Transform the data to ensure metadata is properly parsed
    const interactions: SparcInteraction[] = data.map(item => ({
      ...item,
      metadata: typeof item.metadata === 'string' 
        ? JSON.parse(item.metadata) 
        : item.metadata
    }));

    return interactions;
  } catch (error) {
    console.error('Error fetching session interactions:', error);
    return [];
  }
};

// Get user interaction history - Fixed parameter count
export const getUserInteractionHistory = async (userId: string): Promise<SparcInteraction[]> => {
  try {
    const { data: sessions, error: sessionsError } = await supabase
      .from('sparc_sessions')
      .select('id')
      .eq('user_id', userId);

    if (sessionsError) {
      console.error('Error fetching user sessions:', sessionsError);
      throw sessionsError;
    }

    if (!sessions || sessions.length === 0) {
      return [];
    }

    const sessionIds = sessions.map(session => session.id);

    const { data, error } = await supabase
      .from('sparc_interactions')
      .select('*')
      .in('session_id', sessionIds)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching interactions:', error);
      throw error;
    }

    // Transform the data to ensure metadata is properly parsed
    const interactions: SparcInteraction[] = data.map(item => ({
      ...item,
      metadata: typeof item.metadata === 'string' 
        ? JSON.parse(item.metadata) 
        : item.metadata
    }));

    return interactions;
  } catch (error) {
    console.error('Error fetching interaction history:', error);
    return [];
  }
};

// Get all sessions for a user
export const getUserSessions = async (userId: string): Promise<SparcSession[]> => {
  try {
    const { data, error } = await supabase
      .from('sparc_sessions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user sessions:', error);
      throw error;
    }

    // Transform the data to ensure config is properly parsed
    const sessions: SparcSession[] = data.map(item => ({
      ...item,
      config: typeof item.config === 'string' 
        ? JSON.parse(item.config) 
        : item.config
    }));

    return sessions;
  } catch (error) {
    console.error('Error fetching user sessions:', error);
    return [];
  }
};

// Get a specific session by ID
export const getSessionById = async (sessionId: string): Promise<SparcSession | null> => {
  try {
    const { data, error } = await supabase
      .from('sparc_sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (error) {
      console.error('Error fetching session:', error);
      throw error;
    }

    // Transform the data to ensure config is properly parsed
    const session: SparcSession = {
      ...data,
      config: typeof data.config === 'string' 
        ? JSON.parse(data.config) 
        : data.config
    };

    return session;
  } catch (error) {
    console.error('Error fetching session:', error);
    return null;
  }
};
